import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ChevronRight, Image as ImageIcon } from "lucide-react";
import { fetchPageById, fetchGalleryById } from "../lib/cms";
import { getOptimizedImageUrl } from "../lib/utils";
import Lightbox from "../components/Lightbox";

export default function ProjectDetail() {
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    async function loadProject() {
      setLoading(true);
      // Fetch the special project page
      const pageData = await fetchPageById("horn-vinduesfabrik");
      if (!pageData) {
        navigate("/404", { replace: true });
        return;
      }
      setPage(pageData);

      if (pageData.gallery_slug) {
        const galleryData = await fetchGalleryById(pageData.gallery_slug);
        setGallery(galleryData);
      }
      setLoading(false);
    }
    loadProject();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
      </div>
    );
  }

  if (!page) return null;

  const title = page.title?.da || "Horn vinduesfabrik";
  const contentHtml = page.content?.da || "";
  const metaTitle = page.meta_title?.da || `${title} - Sehested Tømrerfirma ApS`;
  const metaDesc = page.meta_description?.da || "";

  return (
    <div className="bg-primary-50/30 py-12 md:py-20">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
      </Helmet>

      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        <div className="flex flex-wrap items-center gap-2 text-sm text-primary-500 mb-8">
          <Link to="/" className="hover:text-brand-600 transition-colors">Hjem</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link to="/tidligere-projekter" className="hover:text-brand-600 transition-colors">Tidligere projekter</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-primary-800 font-medium">{title}</span>
        </div>

        {/* Back Button */}
        <Link
          to="/tidligere-projekter"
          className="inline-flex items-center gap-2 text-sm font-bold text-brand-700 hover:text-brand-800 mb-6 group"
        >
          <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-1" />
          Tilbage til tidligere projekter
        </Link>

        {/* Project Content */}
        <div className="bg-white rounded-2xl border border-primary-100 p-6 md:p-10 shadow-sm space-y-8">
          <div
            className="rich-text"
            dangerouslySetInnerHTML={{ __html: contentHtml }}
          />

          {/* Gallery Grid */}
          {gallery && gallery.images && gallery.images.length > 0 && (
            <div className="space-y-6 pt-6 border-t border-primary-100">
              <div className="flex items-center gap-3">
                <ImageIcon className="h-6 w-6 text-brand-600" />
                <h2 className="font-display text-xl font-bold text-primary-950">
                  Projektgalleriet
                </h2>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {gallery.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setLightboxIndex(index)}
                    className="group relative aspect-[4/3] overflow-hidden rounded-xl border border-primary-200/60 bg-primary-50 shadow-sm hover:border-brand-500 transition-all duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    <img
                      src={getOptimizedImageUrl(img, { width: 300, height: 225 })}
                      alt={`${title} projektbillede ${index + 1}`}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors duration-300" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {gallery && gallery.images && (
        <Lightbox
          images={gallery.images}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex((prev) => (prev - 1 + gallery.images.length) % gallery.images.length)}
          onNext={() => setLightboxIndex((prev) => (prev + 1) % gallery.images.length)}
        />
      )}
    </div>
  );
}
